import {useState} from "react";
import {usePage} from "@inertiajs/react";
import {PiHeadset} from "react-icons/pi";
import usePollingWhenVisible from "../../hooks/usePollingWhenVisible";
import SharedAccountHint from "./SharedAccountHint.jsx";

/**
 * Fest am unteren Rand der Seitenleiste verankerter Einstieg zu
 * "Tickets". Bewusst KEIN normaler Menüpunkt (siehe MenuService) -
 * stattdessen über den vom Paket vorgesehenen sidebarControls-Slot
 * eingehängt (siehe GlobalControls.jsx). Dieser Slot liegt in der
 * Seitenleiste außerhalb des scrollenden Menübereichs, dadurch bleibt der
 * Einstieg immer sichtbar, auch wenn die Menüliste darüber scrollt.
 *
 * Freischaltung über die bereits geteilte Inertia-Eigenschaft
 * 'supportButtonEnabled' (HandleInertiaRequests) - dieselbe Grundlage
 * (Einstellung SUPPORT_BUTTON_ENABLED), die auch die Support-Schaltfläche
 * in der Kopfleiste steuert.
 *
 * Bewusst ein einfacher <a>-Link statt Inertias <Link>: Die Seitenleiste
 * schließt sich auf schmalen Bildschirmen (Offcanvas-Overlay) nur bei den
 * Menüpunkten der eigentlichen Liste automatisch, weil dort explizit
 * onClick={handleClose} im Paket-Code steht. Dieser Fußbereich hat darauf
 * keinen Zugriff, ohne Paket-Code zu ändern. Ein echter Seitenaufruf räumt
 * das Overlay stattdessen zuverlässig mit ab - denselben Weg geht bereits
 * das Logo/Titel-Element ganz oben in der Leiste (ebenfalls ein einfacher
 * href-Link ohne Inertia-Routing).
 */
export default function SupportSidebarControl() {
    const {supportButtonEnabled, supportTicketUnreadCount, isSharedSupportAccount, path} = usePage().props;
    const isActive = typeof path === "string" && ("/" + path).startsWith("/support/tickets");
    const [sharedHint, setSharedHint] = useState(false);

    // Abzeichen-Zahl lokal halten und auf jeder Seite aktuell halten. Der
    // Initialwert kommt von Inertia; der Poll bringt frische Werte im
    // Hintergrund. Auf der Ticketliste selbst wird nicht gepollt - dort ist
    // das Abzeichen ohnehin ausgeblendet, der Mitarbeiter liest die
    // Antworten gerade. Der Poll pausiert automatisch bei Hintergrund-Tabs.
    const [unread, setUnread] = useState(Number(supportTicketUnreadCount) || 0);
    usePollingWhenVisible(
        supportButtonEnabled && !isActive ? "/support/tickets/stand" : null,
        120000,
        (data) => {
            if (data && data.error !== true) {
                setUnread(Number(data.unreadCount) || 0);
            }
        }
    );

    if (!supportButtonEnabled) {
        return null;
    }

    // Der Sammelzugang der Probearbeiter erstellt hierüber weder Tickets, noch
    // hat er sinnvoll eigene zu lesen - statt der Liste gibt es denselben
    // Hinweis wie am Support-Knopf in der Kopfleiste (SharedAccountHint).
    const handleClick = (event) => {
        if (isSharedSupportAccount) {
            event.preventDefault();
            setSharedHint(true);
        }
    };

    return (
        <>
            <hr className="sidebar-footer-divider" />
            <a
                href="/support/tickets"
                className={`sidebar-footer-link${isActive ? " active" : ""}`}
                onClick={handleClick}
            >
                <PiHeadset size="1.2em" />
                <span>Tickets</span>
                {unread > 0 && (
                    <span
                        className="sidebar-footer-badge"
                        aria-label={`${unread} Ticket${unread === 1 ? "" : "s"} mit neuer Antwort`}
                    >
                        {unread}
                    </span>
                )}
            </a>
            <SharedAccountHint show={sharedHint} onClose={() => setSharedHint(false)} />
        </>
    );
}
